import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";

const getSentenceTokens = (sentence) => {
  if (!sentence || typeof sentence !== "string") return [];
  return sentence
    .trim()
    .split(/\s+/)
    .map((w) => w.trim())
    .filter(Boolean);
};

const normalizeSentenceValue = (sentence) =>
  getSentenceTokens(sentence).join(" ");

const ReArrangeActivity = ({
  currentActivity,
  submitted,
  shuffledWords,
  setShuffledWords,
  handleOptionChange,
  rearrangeSelections,
}) => {
  const isSubmitted = submitted?.isSubmitted || false;

  const userSentence = normalizeSentenceValue(
    rearrangeSelections?.[currentActivity?.ContentId]
  );

  const correctSentences = Array.isArray(currentActivity?.Options)
    ? currentActivity.Options.map((option) =>
        normalizeSentenceValue(option?.Value)
      ).filter(Boolean)
    : [];

  const isCorrect =
    Boolean(userSentence) &&
    correctSentences.some((sentence) => sentence === userSentence);

  const isRtlDirection = currentActivity?.Direction?.toUpperCase() === "RTL";

  const handleDragEnd = (result) => {
    if (!handleOptionChange || submitted?.isSubmitted) return;
    if (!result.destination) return;

    const updated = Array.from(shuffledWords);
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);

    setShuffledWords(updated);

    handleOptionChange(currentActivity.ContentId, 1, updated.join(" "));
  };

  // BACKGROUND RESULT COLOR
  const resultBgColor = isSubmitted
    ? isCorrect
      ? "#e8f5e9"
      : "#ffebee"
    : "transparent";

  return (
    <>
      {/* ---------- DRAG UI ---------- */}
      <Box sx={{ padding: 2, marginTop: 2 }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable
            droppableId="rearrange"
            direction="horizontal"
            isDropDisabled={isSubmitted}
          >
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                dir={isRtlDirection ? "rtl" : "ltr"}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  border: "1px solid #ccc",
                  padding: 2,
                  borderRadius: 2,
                }}
              >
                {shuffledWords.map((word, index) => (
                  <Draggable
                    key={index}
                    draggableId={String(index)}
                    index={index}
                    isDragDisabled={isSubmitted}
                  >
                    {(provided, snapshot) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          padding: "8px 16px",
                          fontSize: "1.5rem",
                          fontFamily: "Traditional Arabic, serif",
                          cursor: isSubmitted ? "not-allowed" : "grab",
                          boxShadow: snapshot.isDragging ? 6 : 2,
                        }}
                      >
                        {word}
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      </Box>

      {/* ---------- RESULT + RATIONALE ---------- */}
      {isSubmitted && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            backgroundColor: resultBgColor, // <<< NOW WORKING
          }}
        >
          {/* Message */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            {isCorrect ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "green" }} />
            ) : (
              <CancelIcon sx={{ fontSize: 40, color: "red" }} />
            )}
          </Box>

          <Typography
            sx={{
              fontWeight: "bold",
              textAlign: "center",
              mt: 1,
              color: isCorrect ? "green" : "red",
            }}
          >
            {isCorrect ? "Correct Answer!" : "Incorrect Answer!"}
          </Typography>

          {/* Rationale */}
          <Box sx={{ mt: 2 }}>
            {currentActivity.Options?.map((opt, idx) => {
              const text = opt.Rationale?.Text?.trim();
              const cid = opt.Rationale?.ContentId;

              return (
                <Box
                  key={idx}
                  sx={{
                    padding: 1,
                    marginTop: 1,
                    border: "1px solid #eee",
                    borderRadius: 2,
                    fontSize: "0.85rem",
                  }}
                >
                  {text ? (
                    <Typography>Rationale: {text}</Typography>
                  ) : cid ? (
                    <Typography>Rationale ID: {cid}</Typography>
                  ) : (
                    <Typography fontStyle="italic">
                      No rationale available.
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

export default ReArrangeActivity;
